import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  BarChart3, Headphones, Mic, BookOpen, PenTool,
  AlertTriangle, Users, TrendingUp, TrendingDown, Layers
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOD_CFG = {
  listening: { icon: Headphones, color: "#002FA7", label: "Listening" },
  speaking: { icon: Mic, color: "#3b82f6", label: "Speaking" },
  reading: { icon: BookOpen, color: "#22c55e", label: "Reading" },
  writing: { icon: PenTool, color: "#f59e0b", label: "Writing" },
};
const MODS = ["listening", "speaking", "reading", "writing"];

const CompareBar = ({ cohorts, mod, maxVal }) => {
  const color = MOD_CFG[mod].color;
  return (
    <div className="space-y-1.5">
      {cohorts.map((c) => {
        const val = c[mod]?.avg || 0;
        const pct = (val / maxVal) * 100;
        return (
          <div key={c.id} className="flex items-center gap-2">
            <span className="text-[10px] w-28 truncate text-right text-gray-500 font-medium" title={c.label}>
              {c.label.length > 18 ? c.label.slice(0, 18) + "..." : c.label}
            </span>
            <div className="flex-1 h-5 bg-gray-100 overflow-hidden relative">
              <div
                className="h-full transition-all duration-500"
                style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.8 }}
              />
              <span className="absolute right-1.5 top-0.5 text-[10px] font-bold text-gray-700">
                {val}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const HeatmapCell = ({ value, maxVal, minVal, mod }) => {
  const range = maxVal - minVal || 1;
  const normalized = (value - minVal) / range;
  const color = MOD_CFG[mod].color;
  const opacity = 0.15 + normalized * 0.6;
  return (
    <td
      className="p-2 text-center text-sm font-bold border border-gray-100"
      style={{ backgroundColor: `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}` }}
      data-testid={`heatmap-cell-${mod}`}
    >
      {value}
    </td>
  );
};

const CohortComparison = () => {
  const [compareBy, setCompareBy] = useState("grade");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("bars");

  useEffect(() => {
    fetchComparison();
  }, [compareBy]);

  const fetchComparison = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/compare`, {
        params: { compare_by: compareBy },
      });
      setData(res.data);
    } catch {
      toast.error("Failed to load comparison data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-gray-400">Loading comparison data...</div>;
  if (!data || !data.cohorts?.length) return <div className="py-12 text-center text-gray-400">No comparison data available</div>;

  const { cohorts, gaps } = data;
  const maxVal = Math.max(
    ...cohorts.flatMap((c) => MODS.map((m) => c[m]?.avg || 0)),
    6
  );

  // Per-modality min/max for heatmap
  const modRanges = {};
  MODS.forEach((mod) => {
    const vals = cohorts.map((c) => c[mod]?.avg || 0).filter((v) => v > 0);
    modRanges[mod] = {
      min: Math.min(...vals, 1),
      max: Math.max(...vals, 6),
    };
  });

  return (
    <div className="space-y-6" data-testid="cohort-comparison">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
            <Layers size={20} className="text-[#002FA7]" />
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
              Cohort Comparison
            </h2>
            <p className="text-sm text-gray-500">
              Compare modality performance across {compareBy === "grade" ? "grade levels" : compareBy === "school" ? "schools" : "classes"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Compare-by toggle */}
          <div className="flex border-2 border-gray-200" data-testid="compare-by-toggle">
            {[
              { key: "grade", label: "By Grade" },
              { key: "school", label: "By School" },
              { key: "class", label: "By Class" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setCompareBy(opt.key)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  compareBy === opt.key
                    ? "bg-[#002FA7] text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
                data-testid={`compare-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* View toggle */}
          <div className="flex border-2 border-gray-200" data-testid="view-toggle">
            {[
              { key: "bars", label: "Bars" },
              { key: "heatmap", label: "Heatmap" },
              { key: "table", label: "Table" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setView(opt.key)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  view === opt.key
                    ? "bg-[#002FA7] text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
                data-testid={`view-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Systemic Gaps Alerts */}
      {gaps.length > 0 && (
        <div className="space-y-2" data-testid="gap-alerts">
          {gaps.map((gap, i) => (
            <div
              key={i}
              className={`flex items-start gap-3 p-3 border-2 ${
                gap.type === "below_average"
                  ? "border-amber-300 bg-amber-50"
                  : "border-red-300 bg-red-50"
              }`}
              data-testid={`gap-alert-${gap.modality}`}
            >
              <AlertTriangle
                size={16}
                className={gap.type === "below_average" ? "text-amber-600 mt-0.5" : "text-red-600 mt-0.5"}
              />
              <div>
                <p className={`text-sm font-semibold ${
                  gap.type === "below_average" ? "text-amber-800" : "text-red-800"
                }`}>
                  {gap.message}
                </p>
                {gap.avg && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Cross-cohort average: {gap.avg}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {MODS.map((mod) => {
          const cfg = MOD_CFG[mod];
          const Icon = cfg.icon;
          const avgs = cohorts.map((c) => c[mod]?.avg || 0).filter((v) => v > 0);
          const crossAvg = avgs.length ? (avgs.reduce((s, v) => s + v, 0) / avgs.length).toFixed(2) : "0";
          const bestCohort = cohorts.reduce((a, b) => ((a[mod]?.avg || 0) > (b[mod]?.avg || 0) ? a : b));
          const worstCohort = cohorts.reduce((a, b) => ((a[mod]?.avg || 0) < (b[mod]?.avg || 0) ? a : b));
          return (
            <div key={mod} className="border-2 border-gray-200 p-4" data-testid={`summary-${mod}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={16} style={{ color: cfg.color }} />
                <span className="text-sm font-semibold">{cfg.label}</span>
              </div>
              <div className="text-2xl font-bold" style={{ color: cfg.color }}>
                {crossAvg}
              </div>
              <div className="text-xs text-gray-500 mb-2">Cross-cohort avg</div>
              <div className="text-[10px] space-y-0.5">
                <div className="flex justify-between">
                  <span className="text-green-600 font-semibold flex items-center gap-0.5">
                    <TrendingUp size={10} /> Best
                  </span>
                  <span className="text-gray-600 truncate ml-1" title={bestCohort.label}>
                    {bestCohort.label} ({bestCohort[mod]?.avg})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-red-500 font-semibold flex items-center gap-0.5">
                    <TrendingDown size={10} /> Needs PD
                  </span>
                  <span className="text-gray-600 truncate ml-1" title={worstCohort.label}>
                    {worstCohort.label} ({worstCohort[mod]?.avg})
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bar Chart View */}
      {view === "bars" && (
        <div className="border-2 border-gray-200 p-5" data-testid="bars-view">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {MODS.map((mod) => (
              <div key={mod}>
                <div className="flex items-center gap-2 mb-3">
                  {React.createElement(MOD_CFG[mod].icon, { size: 14, style: { color: MOD_CFG[mod].color } })}
                  <span className="text-sm font-semibold">{MOD_CFG[mod].label}</span>
                </div>
                <CompareBar cohorts={cohorts} mod={mod} maxVal={maxVal} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Heatmap View */}
      {view === "heatmap" && (
        <div className="border-2 border-gray-200 overflow-x-auto" data-testid="heatmap-view">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#002FA7] text-white">
                <th className="text-left p-3 font-semibold">Cohort</th>
                {MODS.map((mod) => (
                  <th key={mod} className="text-center p-3 font-semibold">{MOD_CFG[mod].label}</th>
                ))}
                <th className="text-center p-3 font-semibold">Overall</th>
                <th className="text-center p-3 font-semibold">Students</th>
              </tr>
            </thead>
            <tbody>
              {cohorts.map((c, idx) => (
                <tr key={c.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                  <td className="p-2 font-medium text-sm border border-gray-100">{c.label}</td>
                  {MODS.map((mod) => (
                    <HeatmapCell
                      key={mod}
                      value={c[mod]?.avg || 0}
                      maxVal={modRanges[mod].max}
                      minVal={modRanges[mod].min}
                      mod={mod}
                    />
                  ))}
                  <td className="p-2 text-center text-sm font-bold border border-gray-100">
                    {c.overall?.avg || 0}
                  </td>
                  <td className="p-2 text-center text-xs text-gray-500 border border-gray-100">
                    <span className="flex items-center justify-center gap-1">
                      <Users size={12} /> {c.student_count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Table View — detailed with growth */}
      {view === "table" && (
        <div className="border-2 border-gray-200 overflow-x-auto" data-testid="table-view">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#002FA7] text-white">
                <th className="text-left p-2 font-semibold" rowSpan={2}>Cohort</th>
                {MODS.map((mod) => (
                  <th key={mod} className="text-center p-2 font-semibold" colSpan={3} style={{ borderLeft: `2px solid ${MOD_CFG[mod].color}` }}>
                    {MOD_CFG[mod].label}
                  </th>
                ))}
                <th className="text-center p-2 font-semibold" rowSpan={2}>Overall</th>
                <th className="text-center p-2 font-semibold" rowSpan={2}>N</th>
              </tr>
              <tr className="bg-[#002FA7]/90 text-white text-[10px]">
                {MODS.map((mod) => (
                  <React.Fragment key={mod}>
                    <th className="p-1 text-center" style={{ borderLeft: `2px solid ${MOD_CFG[mod].color}` }}>Avg</th>
                    <th className="p-1 text-center">Growth</th>
                    <th className="p-1 text-center">Range</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohorts.map((c, idx) => (
                <tr key={c.id} className={`border-b border-gray-100 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                  <td className="p-2 font-medium">{c.label}</td>
                  {MODS.map((mod) => {
                    const m = c[mod] || {};
                    return (
                      <React.Fragment key={mod}>
                        <td className="p-1 text-center font-bold" style={{ borderLeft: `2px solid ${MOD_CFG[mod].color}20`, color: MOD_CFG[mod].color }}>
                          {m.avg || 0}
                        </td>
                        <td className={`p-1 text-center font-semibold ${(m.avg_growth || 0) > 0 ? "text-green-700" : (m.avg_growth || 0) < 0 ? "text-red-600" : "text-gray-400"}`}>
                          {(m.avg_growth || 0) > 0 ? "+" : ""}{m.avg_growth || 0}
                        </td>
                        <td className="p-1 text-center text-gray-400">
                          {m.min}–{m.max}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td className="p-2 text-center font-bold">{c.overall?.avg || 0}</td>
                  <td className="p-2 text-center text-gray-400">{c.student_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CohortComparison;
